/**
 * useMotionFilterState — a sessionStorage-backed, LIVE-SHARED `useState<Option[]>`
 * for the Motions-page filter selections, so they survive both a tab switch AND
 * a round-trip to the Motion detail page (which unmounts MotionsPage — plain
 * React state would be lost). sessionStorage (not localStorage) so the filter
 * clears when the tab/window closes; and (not URL) so tokens keep their
 * {value,label} losslessly (a Topic token shows its name, not the numeric id).
 *
 * WHY a pub/sub store (useSyncExternalStore) and not just useState-seeded-from-
 * storage: MotionsPage (Motion List tab) is the RESIDENT parent — it does NOT
 * unmount when you switch to the Pipeline List tab. So a plain
 * useState(() => load()) would read storage only once at mount and never see a
 * write made by the (separately-mounted) Pipeline tab → Pipeline→List wouldn't
 * propagate. A module-level store with subscribers makes every hook sharing a key
 * re-render on any write, regardless of who is mounted when.
 *
 * SHARING is by key, deliberately:
 *   - the four DOMAIN filters (Industry / Use Case / Topic / Asset) use the SAME
 *     key across both tabs → changing one tab's selection is reflected live in the
 *     other (same "I'm looking at FSI" context), in BOTH directions.
 *   - the Motion filter uses a PER-TAB key → the two tabs' Motion selections stay
 *     independent (their option sets + semantics differ: List = client-side name
 *     filter over loaded rows; Pipeline = server-side motionIds). Independent, but
 *     each still persists across a round-trip.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { MultiselectProps } from '@amzn/awsui-components-react';

export type MotionFilterOption = MultiselectProps.Option;

const KEY_PREFIX = 'ivt.motionFilters.';
const EMPTY: readonly MotionFilterOption[] = [];

// Per-key in-memory cache (the snapshot useSyncExternalStore hands back). Kept
// stable-by-reference so getSnapshot returns the same array until a real change,
// which is required (returning a fresh [] each call would loop React forever).
const cache = new Map<string, readonly MotionFilterOption[]>();
const listeners = new Map<string, Set<() => void>>();

function readStorage(key: string): readonly MotionFilterOption[] {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + key);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const opts = parsed.filter((o): o is MotionFilterOption => !!o && typeof o.value === 'string');
      return opts.length > 0 ? opts : EMPTY;
    }
  } catch {
    /* ignore — corrupt/absent → empty */
  }
  return EMPTY;
}

function getSnapshot(key: string): readonly MotionFilterOption[] {
  if (!cache.has(key)) cache.set(key, readStorage(key)); // lazy init from storage
  return cache.get(key) as readonly MotionFilterOption[];
}

function subscribe(key: string, cb: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => set!.delete(cb);
}

function write(key: string, next: readonly MotionFilterOption[]) {
  cache.set(key, next);
  try {
    sessionStorage.setItem(KEY_PREFIX + key, JSON.stringify(next));
  } catch {
    /* ignore (private mode / quota) — filter just won't persist */
  }
  listeners.get(key)?.forEach((cb) => cb()); // wake every hook sharing this key
}

/** sessionStorage key for the Pipeline View's SFDC PropertyFilter query. It is
 *  NOT an option-array filter (different shape: a PropertyFilterProps.Query), so it
 *  lives outside the MOTION_FILTER_KEYS store; MotionPipelineList reads/writes it and
 *  setOnlyAssetFilter clears it — by storing an EMPTY query, not by removing the key, since
 *  absence now means "seed the default". Single source of truth for the key string. */
export const PIPELINE_PROPERTY_FILTER_STORAGE_KEY = 'ivt.pipeline.propertyFilterQuery';

/** Every filter key the Motions page uses (both tabs). Keep in sync with the
 *  useMotionFilterState('...') calls in MotionsPage / MotionPipelineList. */
export const MOTION_FILTER_KEYS = [
  'industries',
  'useCases',
  'subTopics',
  'assets',
  'list.motions',
  'pipeline.motions',
  // NOTE: 'pipeline.sfdcStages' AND 'pipeline.scores' both removed — SFDC Stage and
  // Engagement Depth Score migrated into the Pipeline View's PropertyFilter (its own
  // sessionStorage key ivt.pipeline.propertyFilterQuery, owned by MotionPipelineList,
  // cleared by setOnlyAssetFilter below), so neither option-array key is used (DAT-8462).
] as const;

/**
 * Reset ALL Motion filters to a single Asset selection — used when deep-linking
 * into the Pipeline List tab from an Asset (Asset Detail → Motion cell). Clears
 * every other filter so the landing view is scoped to JUST that asset, not
 * whatever the user last had selected on the Motions page (those persist in
 * sessionStorage). Writes through `write()` so open subscribers re-render.
 */
export function setOnlyAssetFilter(asset: MotionFilterOption): void {
  for (const key of MOTION_FILTER_KEYS) {
    write(key, key === 'assets' ? [asset] : EMPTY);
  }
  // Also clear the Pipeline View's SFDC PropertyFilter query — it lives in its own
  // sessionStorage key (owned by MotionPipelineList, read on mount), NOT in the
  // MOTION_FILTER_KEYS option-array store. Without this, a stale Stage/Account/… tree
  // filter would survive the Asset→Pipeline deep-link and contradict the "scoped to
  // JUST this asset" landing intent (DAT-8462).
  //
  // ⚠️ Write an EXPLICIT EMPTY QUERY, do not removeItem. MotionPipelineList seeds its
  // own-industry default when this key is ABSENT, and treats a stored empty query as "the
  // user cleared it". Removing the key therefore stopped clearing and started SEEDING —
  // landing the deep-link on an own-industry filter the user never set, under a count
  // (AssetDetail.motionPipeline.taggedCount) that carries no such filter.
  try {
    sessionStorage.setItem(
      PIPELINE_PROPERTY_FILTER_STORAGE_KEY,
      JSON.stringify({ operation: 'and', tokens: [], tokenGroups: [] }),
    );
  } catch {
    /* ignore disabled storage */
  }
}

/**
 * Like useState for a filter's selected options, but backed by a shared,
 * sessionStorage-persisted store. Two hooks with the SAME key share the SAME live
 * value (used to share the domain filters across tabs, both directions);
 * different keys are independent.
 */
export function useMotionFilterState(
  key: string,
): [readonly MotionFilterOption[], (next: readonly MotionFilterOption[]) => void] {
  const value = useSyncExternalStore(
    (cb) => subscribe(key, cb),
    () => getSnapshot(key),
    () => EMPTY, // SSR/no-storage fallback
  );
  const set = useCallback((next: readonly MotionFilterOption[]) => write(key, next), [key]);
  return [value, set];
}

/** Test-only: reset the in-memory store so a test's sessionStorage.clear() isn't
 *  shadowed by a stale cached snapshot from a prior test. */
export function __resetMotionFilterStoreForTesting(): void {
  cache.clear();
  listeners.clear();
}
